import React from 'react';

import { faInfoCircle, faMinusCircle, faPhone, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { PrimaryToggle } from './primary_toggle';
import { CONSENT_FIELDS, ConsentField, Phone, PHONE_TYPES, PhoneType } from './types';
import { humanize } from './utils';

interface PhoneRowProps {
  phone: Phone;
  customerTempId: string;
  showAddButton: boolean;
  showDeleteButton: boolean;
  onUpdateField: (phoneTempId: string, field: keyof Phone, value: string | PhoneType) => void;
  onTogglePrimary: (phoneTempId: string) => void;
  onDelete: (phone: Phone) => void;
  onAdd: () => void;
  onConsentChange: (phoneTempId: string, consentField: ConsentField, checked: boolean) => void;
  onConsentInfoClick: (field: ConsentField) => void;
}

/**
 * Determines if a consent field should be disabled based on dependencies.
 *
 * @param phone - The phone to check.
 * @param consentField - The consent field to check.
 * @returns True if the field should be disabled.
 */
const isConsentFieldDisabled = (phone: Phone, consentField: ConsentField): boolean => {
  if (consentField === 'commercial_sms') {
    return !phone.transactional_sms;
  } else if (consentField === 'commercial_call') {
    return !phone.transactional_call;
  }
  return false;
};

/**
 * Component for rendering a single phone row with all its controls.
 *
 * @param props - The component props.
 * @returns The PhoneRow component.
 */
export const PhoneRow: React.FC<PhoneRowProps> = ({
  phone,
  showAddButton,
  showDeleteButton,
  onUpdateField,
  onTogglePrimary,
  onDelete,
  onAdd,
  onConsentChange,
  onConsentInfoClick,
}) => (
  <tr className="phone-row">
    <td>
      <FontAwesomeIcon icon={faPhone} />
    </td>
    <td>
      <select
        className="form-control"
        value={phone.kind}
        onChange={(e) => onUpdateField(phone.tempId, 'kind', e.target.value as PhoneType)}
        aria-label="Phone type"
      >
        {PHONE_TYPES.map((kind) => (
          <option key={kind} value={kind}>
            {kind}
          </option>
        ))}
      </select>
    </td>
    <td>
      <input
        className="form-control"
        placeholder={phone.id ? 'Edit existing phone' : 'Add new phone'}
        value={phone.number}
        onChange={(e) => onUpdateField(phone.tempId, 'number', e.target.value)}
        type="text"
        aria-label="Phone number"
      />
    </td>
    <td>
      <PrimaryToggle
        isPrimary={phone.primary}
        showToggle={!!phone.number}
        onToggle={() => onTogglePrimary(phone.tempId)}
      />
    </td>
    <td>
      {showDeleteButton && (
        <FontAwesomeIcon
          icon={faMinusCircle}
          onClick={() => onDelete(phone)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onDelete(phone);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Delete phone"
        />
      )}
    </td>
    <td>
      {showAddButton && (
        <FontAwesomeIcon
          icon={faPlusCircle}
          onClick={onAdd}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAdd();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Add phone"
        />
      )}
    </td>
    <td>
      <ul className="consent-fields-list text-left">
        {CONSENT_FIELDS.map((consentField) => (
          <li key={consentField}>
            <label>
              <input
                type="checkbox"
                checked={!!phone[consentField]}
                onChange={(e) => onConsentChange(phone.tempId, consentField, e.target.checked)}
                disabled={isConsentFieldDisabled(phone, consentField)}
              />{' '}
              {humanize(consentField)}
            </label>{' '}
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="consent-info"
              onClick={() => onConsentInfoClick(consentField)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onConsentInfoClick(consentField);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Info about ${humanize(consentField)}`}
            />
          </li>
        ))}
      </ul>
    </td>
  </tr>
);
